import { cn } from '@/lib/utils'
import type { SaveState } from './useResponsesAutosave.js'

interface Props {
  state: SaveState
  /** Save-state error (transport/persistence) — never a validation message. */
  error?: string | null
  onRetry?: () => void
}

/**
 * Persistent autosave indicator (EA-63 / PRD §8.2). Reports save state only —
 * "Saving…", "Saved just now", or a save failure with Retry. It deliberately never
 * surfaces validation problems; those appear only on Mark Complete (EA-62).
 */
export function SaveIndicator({ state, error, onRetry }: Props) {
  if (state === 'idle') return null

  return (
    <div
      aria-live="polite"
      className={cn(
        'flex items-center gap-2 text-xs',
        state === 'error' || state === 'unavailable' ? 'text-danger' : 'text-subtle',
      )}
    >
      {state === 'saving' && <span>Saving…</span>}
      {state === 'saved' && <span>Saved just now</span>}
      {state === 'unavailable' && (
        <span role="alert">That industry is no longer available — please choose again.</span>
      )}
      {state === 'error' && (
        <span role="alert" title={error ?? undefined} className="flex items-center gap-2">
          Couldn’t save your answer.
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="font-medium underline hover:no-underline"
            >
              Retry
            </button>
          )}
        </span>
      )}
    </div>
  )
}
